/**
 * econEventRead — turn a released economic print (actual / consensus / previous, all raw Nasdaq strings)
 * into a plain-English, direction-aware read for the /calendar hover summary.
 *
 * ⭐ HONEST BY CONSTRUCTION (owner 2026-09-06: "master designed ... good or bad ... easy to understand
 * for all trader levels"). "Good/bad" is NOT symmetric across indicators: a hot jobs number is strength,
 * a hot CPI is a headwind. So every indicator is classified by DIRECTION (higher = stronger growth,
 * higher = hotter inflation, or higher = weaker labor) and the read is phrased factually - "stronger /
 * hotter / cooler than expected" - never as investment advice. The optional "market read" line names the
 * common market interpretation (risk-on, rate-cut odds) as context, still not advice.
 *
 * Pure + isomorphic (no 'use client', no server-only imports) so the calendar component - client - can
 * import it directly. Only released events (actual present + parseable) yield a read; unreleased -> null.
 */

export type EconDir = 'growth' | 'inflation' | 'weakHigh' | 'rate' | 'neutral'
export type VerdictTone = 'up' | 'cool' | 'warm' | 'hot' | 'down' | 'flat'
export type DeltaTone = 'up' | 'dn' | 'flat'

export type EconCompare = { label: string; delta: string | null; tone: DeltaTone }
export type EconRead = {
  verdict: { label: string; tone: VerdictTone }
  vsEst: EconCompare | null
  vsPrev: EconCompare | null
  plain: string
  glossary: string
  marketRead: string | null
}

type Parsed = { abs: number; isPct: boolean; display: string }

/** Parse a raw Nasdaq value string ("162K", "3.1%", "-0.4%", "1.2M", "$4.3B", " ") into a number. */
export function parseEconValue(raw: string | null | undefined): Parsed | null {
  if (raw == null) return null
  // Nasdaq uses a literal nbsp / '&nbsp;' / '-' for "no value yet".
  const display = raw.replace(/&nbsp;/gi, ' ').replace(/ /g, ' ').trim()
  if (!display || display === '-' || display === '--') return null
  const isPct = /%/.test(display)
  let s = display.replace(/[%$,\s]/g, '')
  const m = s.match(/^(-?\d+(?:\.\d+)?)([kmb])?$/i)
  if (!m) return null
  let n = parseFloat(m[1])
  if (!Number.isFinite(n)) return null
  const suf = (m[2] || '').toLowerCase()
  const scale = suf === 'b' ? 1e9 : suf === 'm' ? 1e6 : suf === 'k' ? 1e3 : 1
  return { abs: n * scale, isPct, display }
}

const fmtCompact = (n: number): string => {
  const a = Math.abs(n)
  const s = a >= 1e9 ? `${(n / 1e9).toFixed(1)}B` : a >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(n / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K` : `${Math.round(n)}`
  return s.replace(/\.0([KMB]?)$/, '$1')
}

/** Signed delta, formatted in the metric's own unit (percentage points for %, compact counts otherwise). */
function fmtDelta(delta: number, isPct: boolean): string {
  const near0 = isPct ? Math.abs(delta) < 0.05 : Math.abs(delta) < 0.5
  if (near0) return isPct ? '0.0pp' : '0'
  const sign = delta > 0 ? '+' : '-'
  return isPct ? `${sign}${Math.abs(delta).toFixed(1)}pp` : `${sign}${fmtCompact(Math.abs(delta))}`
}

type IndInfo = { dir: EconDir; glossary: string; pos: string | null; neg: string | null }

// Keyword table for the high-impact US releases the calendar whitelists. Order matters: more specific
// patterns first. Every entry carries the direction, a one-line beginner glossary, and the two market
// reads (pos = actual surprised HIGH vs the estimate, neg = surprised LOW).
const INDICATORS: { re: RegExp; info: IndInfo }[] = [
  { re: /FOMC|federal funds|interest rate|fed(eral)? (chair|reserve|rate)/i, info: { dir: 'rate', glossary: "The Federal Reserve's policy interest-rate decision.", pos: 'A higher-than-expected rate is hawkish, generally a headwind for stocks.', neg: 'A lower-than-expected rate is dovish, generally risk-on.' } },
  { re: /core cpi|\bcpi\b|consumer price|inflation rate/i, info: { dir: 'inflation', glossary: 'Consumer Price Index, the main gauge of consumer inflation.', pos: 'Hotter inflation pressures the Fed to hold rates higher for longer.', neg: 'Cooler inflation supports the case for rate cuts.' } },
  { re: /core ppi|\bppi\b|producer price/i, info: { dir: 'inflation', glossary: 'Producer Price Index, inflation at the wholesale level (often a preview of CPI).', pos: 'Hotter wholesale prices can feed through to consumer inflation.', neg: 'Softer wholesale prices ease the inflation outlook.' } },
  { re: /\bpce\b|personal consumption expenditures/i, info: { dir: 'inflation', glossary: "PCE, the Fed's preferred inflation gauge.", pos: 'A hotter PCE is a headwind for rate cuts.', neg: 'A cooler PCE supports rate-cut expectations.' } },
  { re: /hourly earnings/i, info: { dir: 'inflation', glossary: 'Average hourly earnings, wage growth and a key inflation input.', pos: 'Faster wage growth adds to inflation pressure.', neg: 'Slower wage growth eases inflation pressure.' } },
  { re: /unit labor cost/i, info: { dir: 'inflation', glossary: 'Labor cost per unit of output, an inflation signal.', pos: 'Rising labor costs add to inflation pressure.', neg: 'Falling labor costs ease it.' } },
  { re: /unemployment rate/i, info: { dir: 'weakHigh', glossary: 'Share of the labor force without a job and actively looking. Higher is weaker.', pos: 'A rising rate signals a softening labor market, and can revive rate-cut bets.', neg: 'A falling rate signals labor-market strength.' } },
  { re: /jobless claims|initial claims|continuing claims/i, info: { dir: 'weakHigh', glossary: 'Weekly filings for unemployment benefits. Higher is weaker.', pos: 'More claims point to a cooling labor market.', neg: 'Fewer claims point to a resilient labor market.' } },
  { re: /nonfarm|non-farm|payroll/i, info: { dir: 'growth', glossary: 'Net new jobs added last month, the most-watched labor gauge.', pos: 'A strong jobs number signals a healthy economy, but can cool near-term rate-cut hopes.', neg: 'A weak jobs number raises growth worries but can boost rate-cut odds.' } },
  { re: /jolts|job openings/i, info: { dir: 'growth', glossary: 'Job openings, a read on labor demand.', pos: 'More openings signal firm labor demand.', neg: 'Fewer openings signal cooling demand.' } },
  { re: /\bgdp\b|gross domestic/i, info: { dir: 'growth', glossary: 'Gross Domestic Product, the broadest measure of economic growth.', pos: 'Stronger growth is risk-on, though very hot data can lift rate expectations.', neg: 'Softer growth raises slowdown worries.' } },
  { re: /retail sales/i, info: { dir: 'growth', glossary: 'Consumer spending at retailers, a real-time pulse on demand.', pos: 'Strong spending signals a resilient consumer.', neg: 'Weak spending signals a cautious consumer.' } },
  { re: /\bism\b|manufacturing pmi|services pmi|non-manufacturing|purchasing managers/i, info: { dir: 'growth', glossary: 'A survey of business activity; above 50 signals expansion.', pos: 'An expanding reading points to healthy activity.', neg: 'A contracting reading points to slowing activity.' } },
  { re: /consumer confidence|michigan|sentiment/i, info: { dir: 'growth', glossary: 'How optimistic consumers feel about the economy.', pos: 'Rising confidence supports future spending.', neg: 'Falling confidence signals caution ahead.' } },
  { re: /durable goods|factory orders|industrial production|capacity util/i, info: { dir: 'growth', glossary: 'A gauge of manufacturing and business investment.', pos: 'Stronger output points to healthy activity.', neg: 'Weaker output points to a slowdown.' } },
  { re: /(existing|new) home sales|housing starts|building permits/i, info: { dir: 'growth', glossary: 'A read on the housing market and construction.', pos: 'A firmer housing read supports growth.', neg: 'A softer housing read weighs on growth.' } },
  { re: /personal (income|spending)/i, info: { dir: 'growth', glossary: 'Household income and spending, a measure of consumer strength.', pos: 'Stronger spending signals a resilient consumer.', neg: 'Weaker spending signals caution.' } },
  { re: /trade balance/i, info: { dir: 'neutral', glossary: 'The gap between what the country exports and imports.', pos: null, neg: null } },
]

const FALLBACK: IndInfo = { dir: 'neutral', glossary: 'A U.S. macroeconomic release.', pos: null, neg: null }

export function classifyIndicator(name: string): IndInfo {
  for (const { re, info } of INDICATORS) if (re.test(name)) return info
  return FALLBACK
}

// "good for the market" direction of a POSITIVE surprise, per indicator direction.
const goodWhenHigh = (dir: EconDir): boolean => dir === 'growth'
const deltaToneFor = (dir: EconDir, sign: number): DeltaTone => {
  if (sign === 0) return 'flat'
  if (dir === 'neutral') return 'flat'
  const up = sign > 0
  return (goodWhenHigh(dir) ? up : !up) ? 'up' : 'dn'
}

/** Build the human read for one released economic event. Returns null when there is no usable actual. */
export function readEconEvent(ev: { event: string; actual: string | null; consensus: string | null; previous: string | null; released?: boolean }): EconRead | null {
  const act = parseEconValue(ev.actual)
  if (!act) return null // not released, or an unparseable actual — no read to give
  const est = parseEconValue(ev.consensus)
  const prev = parseEconValue(ev.previous)
  const info = classifyIndicator(ev.event)
  const { dir } = info

  // The surprise (vs estimate) is what moves markets; fall back to vs-previous when there is no estimate.
  const hasEst = !!est
  const surprise = hasEst ? act.abs - est!.abs : null
  const changePrev = prev ? act.abs - prev.abs : null
  const inLineTol = act.isPct ? 0.05 : Math.max(0.5, Math.abs((est?.abs ?? prev?.abs ?? 0)) * 0.005)
  const surpriseSign = surprise == null ? null : Math.abs(surprise) < inLineTol ? 0 : surprise > 0 ? 1 : -1
  const prevSign = changePrev == null ? null : Math.abs(changePrev) < inLineTol ? 0 : changePrev > 0 ? 1 : -1

  // The verdict is driven by the surprise vs estimate; if there is no estimate, by the change vs previous.
  const drivingSign = surpriseSign != null ? surpriseSign : (prevSign ?? null)
  const big = surprise != null && est ? Math.abs(surprise) >= Math.max(Math.abs(est.abs) * 0.5, inLineTol * 4) : false

  const verdict = buildVerdict(dir, drivingSign, big, hasEst)

  const vsEst: EconCompare | null = est
    ? { label: surpriseSign === 0 ? 'In line' : surprise! > 0 ? 'Above' : 'Below', delta: fmtDelta(surprise!, act.isPct), tone: deltaToneFor(dir, surpriseSign!) }
    : null
  const vsPrev: EconCompare | null = prev
    ? { label: prevSign === 0 ? 'Unchanged' : changePrev! > 0 ? 'Higher' : 'Lower', delta: fmtDelta(changePrev!, act.isPct), tone: deltaToneFor(dir, prevSign!) }
    : null

  const plain = buildPlain(dir, drivingSign, act, est, prev, prevSign)
  const marketRead = drivingSign == null || drivingSign === 0
    ? (drivingSign === 0 ? 'The print matched expectations, so little surprise for markets.' : null)
    : (drivingSign > 0 ? info.pos : info.neg)

  return { verdict, vsEst, vsPrev, plain, glossary: info.glossary, marketRead }
}

function buildVerdict(dir: EconDir, sign: number | null, big: boolean, hasEst: boolean): { label: string; tone: VerdictTone } {
  const much = big ? 'Much ' : ''
  if (sign == null) return { label: 'Released', tone: 'flat' }
  if (sign === 0) return { label: hasEst ? 'In line with forecasts' : 'Unchanged', tone: 'flat' }
  const hi = sign > 0
  switch (dir) {
    case 'growth': return hi ? { label: `${much}stronger than expected`, tone: 'up' } : { label: `${much}weaker than expected`, tone: 'down' }
    case 'inflation': return hi ? { label: `${much || 'A touch '}hotter than expected`, tone: big ? 'hot' : 'warm' } : { label: `${much}cooler than expected`, tone: 'cool' }
    case 'weakHigh': return hi ? { label: `${much}weaker than expected`, tone: 'down' } : { label: `${much}stronger than expected`, tone: 'up' }
    case 'rate': return hi ? { label: 'More hawkish than expected', tone: 'warm' } : { label: 'More dovish than expected', tone: 'up' }
    default: return hi ? { label: 'Above forecast', tone: 'flat' } : { label: 'Below forecast', tone: 'flat' }
  }
}

function buildPlain(dir: EconDir, sign: number | null, act: Parsed, est: Parsed | null, prev: Parsed | null, prevSign: number | null): string {
  const lead = (() => {
    if (sign == null) return 'This release is out'
    if (sign === 0) return est ? 'Landed in line with forecasts' : 'Came in unchanged'
    const hi = sign > 0
    switch (dir) {
      case 'growth': return hi ? 'Came in above forecasts' : 'Fell short of forecasts'
      case 'inflation': return hi ? 'Ran hotter than expected' : 'Came in cooler than expected'
      case 'weakHigh': return hi ? 'Came in higher than forecast, a softer read' : 'Came in lower than forecast, a firmer read'
      case 'rate': return hi ? 'A more hawkish decision than expected' : 'A more dovish decision than expected'
      default: return hi ? 'Printed above forecast' : 'Printed below forecast'
    }
  })()
  const vsEstClause = est ? ` versus the ${est.display} expected` : ''
  const prevClause = prev == null || prevSign == null ? ''
    : prevSign === 0 ? ', unchanged from last period'
    : prevSign > 0 ? `, up from ${prev.display}` : `, down from ${prev.display}`
  return `${lead}: ${act.display}${vsEstClause}${prevClause}.`
}
